import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { ApiResponse, PageableDto } from '@shared-models';
import { API_CONFIG, ApiConfig } from '../../api-config';
import { mapMutationMessage } from '../../../utils/map-pageable';

/** Shape API de un documento de país. */
export interface CountryDocumentDto {
  id: string;
  countryId?: string;
  typeDocumentId?: string;
  typeDocumentPrefix?: string;
  typeDocumentCode?: string;
  typeDocumentName?: string;
  name: string;
  fileUrl?: string;
  originalName?: string;
  contentType?: string;
  fileSize?: number;
  status?: string;
  createdAt?: string;
  updatedAt?: string;
}

export type CountryDocumentWritePayload = {
  name: string;
  typeDocumentId: string;
};

export type CountryDocumentListParams = {
  page?: number;
  size?: number;
  sort?: string;
  direction?: 'asc' | 'desc';
  status?: string;
  search?: string;
};

/**
 * Documentos de país.
 *   GET    /api/settings/countries/{countryId}/documents
 *   POST   /api/settings/countries/{countryId}/documents  (form-data)
 *   PUT    /api/settings/countries/{countryId}/documents/{id}
 *   PATCH  /api/settings/countries/{countryId}/documents/{id}/status
 *   DELETE /api/settings/countries/{countryId}/documents/{id}
 */
@Injectable({ providedIn: 'root' })
export class CountryDocumentService {
  private readonly http = inject(HttpClient);
  private readonly apiConfig = inject<ApiConfig>(API_CONFIG);

  private resource(countryId: string): string {
    return `${this.apiConfig.apiUrl}/settings/countries/${countryId}/documents`;
  }

  list(
    countryId: string,
    params: CountryDocumentListParams = {},
  ): Observable<CountryDocumentDto[]> {
    let httpParams = new HttpParams()
      .set('page', String(params.page ?? 0))
      .set('size', String(params.size ?? 100))
      .set('sort', params.sort ?? 'createdAt')
      .set('direction', params.direction ?? 'desc');

    if (params.status?.trim()) {
      httpParams = httpParams.set('status', params.status.trim());
    }
    if (params.search?.trim()) {
      httpParams = httpParams.set('search', params.search.trim());
    }

    return this.http
      .get<
        ApiResponse<CountryDocumentDto[] | PageableDto<CountryDocumentDto>>
      >(this.resource(countryId), { params: httpParams })
      .pipe(map((res) => this.unwrapList(res.data)));
  }

  create(
    countryId: string,
    payload: CountryDocumentWritePayload,
    file: File,
  ): Observable<{ message: string }> {
    return this.http
      .post<{ success?: boolean; message?: string }>(
        this.resource(countryId),
        this.toFormData(payload, file),
      )
      .pipe(
        map((response) => mapMutationMessage(response, 'Documento creado')),
      );
  }

  update(
    countryId: string,
    documentId: string,
    payload: CountryDocumentWritePayload,
    file?: File | null,
  ): Observable<{ message: string }> {
    return this.http
      .put<{ success?: boolean; message?: string }>(
        `${this.resource(countryId)}/${documentId}`,
        this.toFormData(payload, file),
      )
      .pipe(
        map((response) =>
          mapMutationMessage(response, 'Documento actualizado'),
        ),
      );
  }

  updateStatus(
    countryId: string,
    documentId: string,
    status: 'active' | 'inactive',
  ): Observable<{ message: string }> {
    const apiStatus = status === 'inactive' ? 'INACTIVE' : 'ACTIVE';
    return this.http
      .patch<{ success?: boolean; message?: string }>(
        `${this.resource(countryId)}/${documentId}/status`,
        { status: apiStatus },
      )
      .pipe(
        map((response) =>
          mapMutationMessage(response, 'Estatus actualizado exitosamente'),
        ),
      );
  }

  delete(
    countryId: string,
    documentId: string,
  ): Observable<{ message: string }> {
    return this.http
      .delete<{ success?: boolean; message?: string }>(
        `${this.resource(countryId)}/${documentId}`,
      )
      .pipe(
        map((response) =>
          mapMutationMessage(response, 'Documento eliminado exitosamente'),
        ),
      );
  }

  private toFormData(
    payload: CountryDocumentWritePayload,
    file?: File | null,
  ): FormData {
    const formData = new FormData();
    formData.append('name', payload.name);
    formData.append('typeDocumentId', payload.typeDocumentId);
    if (file) {
      formData.append('file', file);
    }
    return formData;
  }

  private unwrapList(
    data:
      | CountryDocumentDto[]
      | PageableDto<CountryDocumentDto>
      | null
      | undefined,
  ): CountryDocumentDto[] {
    if (Array.isArray(data)) return data;
    if (data && Array.isArray(data.content)) return data.content;
    return [];
  }
}
